import { useEffect, useState } from "react";
import { Loader2, Languages, Save, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Field, Lbl, ImageSlot, PdfSlot, inp } from "./WpFields";
import WpRealizzazioni from "./WpRealizzazioni";

const SERV = { fv_residenziale: "FV residenziale", fv_aziende: "FV aziende", fv_agricolo: "FV agricolo", accumulo: "Accumulo", colonnine_ev: "Colonnine EV",
  pompe_calore: "Pompe di calore", manutenzione: "Manutenzione", pratiche_incentivi: "Pratiche e incentivi", monitoraggio: "Monitoraggio", rimozione_amianto: "Rimozione amianto" };
const TABS = [["pagina", "Pagina"], ["anagrafica", "Anagrafica"], ["hero", "Hero"], ["chi", "Chi siamo"], ["servizi", "Servizi"], ["real", "Realizzazioni"]];
const toList = (v) => (Array.isArray(v) ? v.join("\n") : v || "");

const Sec = ({ t, f, set, setSoc, setArr, up, d, translate, tr, prodotti }) => {
  if (t === "pagina") return (
    <div className="space-y-3">
      <Field label="Slug pagina" hint="minuscolo, con trattini" value={f.slug} onChange={(v) => set("slug")(v.toLowerCase().replace(/[^a-z0-9-]+/g, "-"))} disabled={!d.staff} testid="wp-slug" />
      <label className={`flex items-center gap-2 text-sm ${d.staff ? "" : "opacity-60"}`}>
        <input type="checkbox" checked={!!f.page_enabled} disabled={!d.staff} onChange={(e) => set("page_enabled")(e.target.checked)} data-testid="wp-page-enabled" />
        Pagina pubblicata su torri.solar {d.staff ? "" : "(gestita da Torri)"}
      </label>
      {d.issues.length > 0
        ? <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-300" data-testid="wp-issues"><div className="mb-1 flex items-center gap-1.5 font-bold"><AlertTriangle className="h-4 w-4" />Da completare per pubblicare</div>{d.issues.map((x) => <div key={x}>· {x}</div>)}</div>
        : <div className="flex items-center gap-1.5 text-xs text-green-400" data-testid="wp-complete"><CheckCircle2 className="h-4 w-4" />Profilo completo</div>}
      {d.updated_at && <p className="text-[11px] text-neutral-500" data-testid="wp-updated">Ultima modifica {new Date(d.updated_at).toLocaleString("it-IT")}{d.updated_by ? ` · ${d.updated_by}` : ""}</p>}
    </div>
  );
  if (t === "anagrafica") return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Ragione sociale" value={f.ragione_sociale} onChange={set("ragione_sociale")} testid="wp-ragione-sociale" />
      <Field label="Partita IVA" value={f.piva} onChange={set("piva")} testid="wp-piva" />
      <Field label="WhatsApp" value={f.whatsapp} onChange={set("whatsapp")} placeholder="+39…" testid="wp-whatsapp" />
      <Field label="Email per i contatti" hint="se diversa dall'email" value={f.email_lead} onChange={set("email_lead")} testid="wp-email-lead" />
      <Field label="Sito web" value={f.sito_web} onChange={set("sito_web")} testid="wp-sito-web" />
      {["facebook", "instagram", "linkedin", "youtube"].map((k) => <Field key={k} label={k} value={f.social?.[k]} onChange={setSoc(k)} placeholder="https://…" testid={`wp-social-${k}`} />)}
      <div className="sm:col-span-2"><Field label="Orari" area rows={3} value={f.orari} onChange={set("orari")} placeholder="Lun–Ven 8:30–12:30 / 14:00–18:00" testid="wp-orari" /></div>
    </div>
  );
  if (t === "hero") return (
    <div className="space-y-4">
      <ImageSlot url={f.hero_image_url} onChange={set("hero_image_url")} up={up} kind="hero" label="Foto hero" hint="orizzontale, almeno 1920 px" wide testid="wp-hero" />
      <Field label="Claim" hint={`${(f.claim || "").length}/140 · facoltativo`} value={f.claim} onChange={set("claim")} maxLength={140} testid="wp-claim" />
    </div>
  );
  if (t === "chi") return (
    <div className="space-y-3">
      <Field label="Presentazione" hint={`${(f.presentazione || "").length}/1200 · min 300`} area rows={6} maxLength={1200} value={f.presentazione} onChange={set("presentazione")} testid="wp-presentazione" />
      <button type="button" onClick={translate} disabled={tr} data-testid="wp-translate"
        className="inline-flex items-center gap-1.5 rounded-full border border-[#C17F59] px-3 py-1.5 text-xs font-semibold text-[#E8B48F] transition-colors hover:bg-[#C17F59]/15">
        {tr ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Languages className="h-3.5 w-3.5" />}Traduci con AI in EN, DE, FR</button>
      {["en", "de", "fr"].map((l) => <Field key={l} label={`Presentazione ${l.toUpperCase()}`} area rows={3} value={f[`presentazione_${l}`]} onChange={set(`presentazione_${l}`)} testid={`wp-presentazione-${l}`} />)}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Anno di fondazione" value={f.anno_fondazione || ""} onChange={set("anno_fondazione")} inputMode="numeric" testid="wp-anno-fondazione" />
        <div />
        <Field label="Area servita" hint="un comune o provincia per riga" area rows={4} value={toList(f.area_servita)} onChange={setArr("area_servita")} testid="wp-area-servita" />
        <Field label="Certificazioni" hint="una per riga" area rows={4} value={toList(f.certificazioni)} onChange={setArr("certificazioni")} testid="wp-certificazioni" />
      </div>
      <ImageSlot url={f.team_photo_url} onChange={set("team_photo_url")} up={up} label="Foto del team" wide testid="wp-team" />
      <PdfSlot url={f.company_profile_pdf_url} onChange={set("company_profile_pdf_url")} up={up} testid="wp-pdf" />
    </div>
  );
  if (t === "servizi") return (
    <div className="flex flex-wrap gap-2" data-testid="wp-servizi">
      {d.servizi.map((s) => {
        const on = (f.servizi || []).includes(s);
        return <button key={s} type="button" onClick={() => set("servizi")(on ? f.servizi.filter((x) => x !== s) : [...(f.servizi || []), s])} data-testid={`wp-serv-${s}`}
          className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "border-[#C17F59] bg-[#C17F59] text-black" : "border-neutral-700 text-neutral-300 hover:border-[#C17F59]"}`}>{SERV[s] || s}</button>;
      })}
    </div>
  );
  return <WpRealizzazioni list={f.realizzazioni || []} onChange={set("realizzazioni")} up={up} prodotti={prodotti} />;
};

export default function WebProfileEditor({ base, params = {}, data, onSaved }) {
  const [d, setD] = useState(data);
  const [f, setF] = useState(data.profile);
  const [tab, setTab] = useState("pagina");
  const [saving, setSaving] = useState(false);
  const [tr, setTr] = useState(false);
  const [prodotti, setProdotti] = useState([]);
  useEffect(() => { api.get("/web-profile/prodotti").then((r) => setProdotti(r.data.items)).catch(() => {}); }, []);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const setSoc = (k) => (v) => setF((x) => ({ ...x, social: { ...(x.social || {}), [k]: v } }));
  const setArr = (k) => (v) => setF((x) => ({ ...x, [k]: v.split("\n") }));
  const up = { url: `${base}/web-profile/upload`, params };
  const save = async () => {
    setSaving(true);
    const body = { ...f, area_servita: (f.area_servita || []).filter((x) => x.trim()), certificazioni: (f.certificazioni || []).filter((x) => x.trim()) };
    try { const r = await api.put(`${base}/web-profile`, body, { params }); setD(r.data); setF(r.data.profile); toast.success("Web Profile salvato e online"); onSaved?.(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Salvataggio non riuscito"); }
    setSaving(false);
  };
  const translate = async () => {
    setTr(true);
    try { const r = await api.post("/web-profile/translate", { testo: f.presentazione }, { timeout: 90000 }); setF((x) => ({ ...x, presentazione_en: r.data.en, presentazione_de: r.data.de, presentazione_fr: r.data.fr })); toast.success("Traduzioni pronte: controllale e salva"); }
    catch (e) { toast.error(e.response?.data?.detail || "Traduzione non riuscita"); }
    setTr(false);
  };
  return (
    <div className="space-y-4 text-white" data-testid="wp-editor">
      <div className="flex flex-wrap gap-1.5 border-b border-neutral-800 pb-3">
        {TABS.map(([k, l]) => <button key={k} type="button" onClick={() => setTab(k)} data-testid={`wp-tab-${k}`}
          className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${tab === k ? "bg-[#C17F59] text-black" : "text-neutral-400 hover:text-white"}`}>{l}</button>)}
      </div>
      <Sec t={tab} f={f} set={set} setSoc={setSoc} setArr={setArr} up={up} d={d} translate={translate} tr={tr} prodotti={prodotti} />
      <div className="sticky bottom-0 -mx-1 flex justify-end border-t border-neutral-800 bg-neutral-950/95 px-1 py-3 backdrop-blur">
        <button type="button" onClick={save} disabled={saving} data-testid="wp-save"
          className="inline-flex items-center gap-2 rounded-full bg-[#C17F59] px-5 py-2 text-sm font-bold text-black transition-opacity hover:opacity-90 disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Salva</button>
      </div>
    </div>
  );
}
export { Lbl, inp };
